import { useEffect, useState } from "react";
import { Seo, localBusinessLD } from "../lib/seo";
import { api } from "../lib/api";
import { Reveal, SectionHead } from "../components/Reveal";
import { PageHero, CtaBand } from "../components/Shared";

const GOOGLE_FICHE_URL =
    "https://www.google.com/search?q=Solino+Pro+Chaumont-en-Vexin+avis";

export default function AvisPage() {
    const [google, setGoogle] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = (delay) =>
            setTimeout(() => {
                if (cancelled) return;
                api.get("/avis")
                    .then((r) => { if (!cancelled) setGoogle(r.data.google || null); })
                    .catch(() => {});
            }, delay);
        load(0);
        load(3500);
        return () => { cancelled = true; };
    }, []);

    const hasReviews = Boolean(google && Array.isArray(google.reviews) && google.reviews.length);
    const aggregate = hasReviews ? { ratingValue: google.rating, reviewCount: google.userRatingCount } : null;

    return (
        <>
            <Seo
                title="Avis clients Google — Solino Pro, peintre & sols à Chaumont-en-Vexin"
                description="Note et avis clients de Solino Pro affichés en direct par Google : peinture, parquet, carrelage, rénovation intérieure. Lisez les vrais avis et laissez le vôtre sur Google Business Profile."
                path="/avis"
                jsonld={[localBusinessLD(aggregate)]}
            />
            <PageHero
                img="https://images.unsplash.com/photo-1630699375895-fe5996d163ee?q=80&w=1800&auto=format&fit=crop"
                crumb="Solino Pro — Confiance"
                title="La parole aux"
                serif="chantiers finis."
            />

            <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 lg:py-28" data-testid="avis-section">
                <SectionHead
                    eyebrow="Avis Google & bouche-à-oreille"
                    title="Nous jouons"
                    serif="carte sur table."
                    text="Pas d'avis copiés-collés ni arrangés : seuls les avis que Google affiche sur notre fiche apparaissent ici — rien d'autre."
                />

                {hasReviews && (
                    <div className="mt-12 flex flex-wrap items-center gap-4" data-testid="avis-google-summary">
                        <span className="inline-flex items-center gap-3 border border-gold/40 bg-gold/5 px-6 py-3">
                            <span className="font-display italic text-3xl text-gold">{google.rating}</span>
                            <span className="text-gold tracking-widest" aria-hidden="true">★★★★★</span>
                            <span className="text-xs uppercase tracking-[0.2em] text-ivory/70">{google.userRatingCount} avis Google</span>
                        </span>
                    </div>
                )}

                {hasReviews ? (
                    <div className="mt-10 grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="avis-google-reviews">
                        {google.reviews.map((r, i) => (
                            <Reveal key={`${r.name}-${i}`} delay={i * 0.07}>
                                <figure className="h-full border border-linew bg-coal p-8 flex flex-col" data-testid={`avis-card-${i}`}>
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="text-gold tracking-widest" aria-label={`${r.rating} étoiles`}>
                                            {"★".repeat(Math.round(r.rating || 5))}
                                        </span>
                                        {r.authorUri && (
                                            <a href={r.authorUri} target="_blank" rel="noopener noreferrer" className="text-[10px] uppercase tracking-[0.2em] text-smoke hover:text-gold transition-colors" data-testid={`avis-author-link-${i}`}>
                                                Profil Google
                                            </a>
                                        )}
                                    </div>
                                    <blockquote className="mt-5 flex-1 text-ivory/85 leading-relaxed">
                                        {r.text ? `« ${r.text} »` : "Avis laissé sur Google."}
                                    </blockquote>
                                    <figcaption className="mt-7 pt-5 border-t border-white/5 text-xs uppercase tracking-[0.2em] text-smoke">
                                        {r.name || "Client Google"}
                                    </figcaption>
                                    {r.relativeTime && (
                                        <p className="mt-2 text-[11px] text-smoke/70">
                                            {r.relativeTime}
                                        </p>
                                    )}
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                ) : (
                    <Reveal delay={0.1}>
                        <p className="mt-10 text-sm text-smoke" data-testid="avis-unavailable">
                            Nos avis Google ne peuvent pas être affichés en direct pour l'instant —
                            retrouvez-les en un clic sur notre fiche ci-dessous.
                        </p>
                    </Reveal>
                )}

                {hasReviews && (
                    <Reveal delay={0.15}>
                        <p className="mt-8 text-xs text-smoke">
                            Avis fournis par Google — affichés par ordre de pertinence. Les avis ne sont pas vérifiés par Google.
                        </p>
                    </Reveal>
                )}

                <Reveal delay={0.2}>
                    <div className="mt-12 flex flex-col sm:flex-row gap-4" data-testid="avis-actions">
                        <a
                            href={google?.googleMapsUri || GOOGLE_FICHE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-3 rounded-full bg-gold text-ink font-bold px-9 py-4 hover:scale-[1.04] transition-transform"
                            data-testid="avis-google-link"
                        >
                            Laissez-nous un avis Google
                        </a>
                        <a
                            href={google?.googleMapsUri || GOOGLE_FICHE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center rounded-full border border-gold/60 text-gold px-9 py-4 hover:bg-gold/10 transition-colors"
                            data-testid="avis-campaign-cta"
                        >
                            Voir notre fiche Google
                        </a>
                    </div>
                </Reveal>

                <Reveal delay={0.25}>
                    <div className="mt-12 border border-gold/30 bg-gold/5 p-8" data-testid="avis-campaign">
                        <h3 className="text-xl text-ivory font-medium">Vous êtes un ancien client ?</h3>
                        <p className="mt-2 text-sm text-smoke leading-relaxed max-w-2xl">
                            Votre avis compte doublement : il nous aide à progresser, et il rassure
                            les futurs clients qui hésitent. Le bouton ci-dessus ouvre directement notre fiche Google —
                            quelques lignes et des étoiles suffisent.
                        </p>
                    </div>
                </Reveal>
            </section>

            <CtaBand />
        </>
    );
}
